/**
 * One Review Writer (P1): an inline ask's link to the community writer, when the flag hands that
 * ask off (`NEXT_PUBLIC_OWNER_WRITER_HANDOFF`, `handoffEnabled`). Null = the ask stays inline,
 * exactly as before — the flag is off for it, or there is no product slug to write about.
 *
 * `from` is the page's own path (plus `hash`), read from `window.location` on every render after
 * mount: the hosts render without a router in their server specs. The first render — the server's and the hydrating one —
 * carries no `from`; the next one adds it, so hydration never sees two different hrefs. Read at
 * render, not once: a host the pages router reuses across a client-side navigation (product to
 * product) must link the page it is on now, and the router updates the address before it renders.
 */
import { useEffect, useMemo, useState } from 'react'
import { publicConfig } from 'config'
import {
  handoffEnabled,
  handoffHint,
  prefersNewTab,
  productSlugFromHref,
  writerHref,
  type WriterSource,
} from './writer-handoff'

/**
 * Same tab on the server pass and on phones; a desktop opens a new tab after mount — the
 * convention of every community link that leaves an owner surface (the accept result, the
 * reward screen's ask, the review row's "Reply →"). The server HTML is always same-tab.
 */
export function usePrefersNewTab(): boolean {
  const [newTab, setNewTab] = useState(false)
  useEffect(() => {
    setNewTab(prefersNewTab(window))
  }, [])
  return newTab
}

/** Spread on such a link: `target="_blank"` + `rel` in new-tab mode, nothing otherwise. */
export function newTabProps(newTab: boolean): { target?: string; rel?: string } {
  return newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {}
}

export interface WriterHandoffLink {
  /** The writer's URL for this ask; `buyAgain` = the strip's tapped answer. */
  href: (buyAgain?: boolean) => string
  /** Desktop, after mount: the writer opens in a new tab (`&tab=new` in `href`). */
  newTab: boolean
  /** Spread on the link: `target="_blank" rel="noopener"` in new-tab mode, nothing otherwise. */
  linkProps: { target?: '_blank'; rel?: string }
  /** The muted line that says what opens (`handoffHint`). */
  hint: string
}

/**
 * `productHref`: the product page of the product the ask is about (`/product/<slug>`) — known
 * at render, so the server HTML already carries the link; mount only adds `from`.
 * `options.flag`: the flag key that gates this ask when it is not the `src` itself — a guide's
 * asks report `strip` / `roster` / `seated` / `rail` but are all switched by `guide`.
 */
export function useWriterHandoff(
  src: WriterSource,
  productHref: string | null | undefined,
  options: { flag?: WriterSource; hash?: string } = {}
): WriterHandoffLink | null {
  const { flag = src, hash } = options
  const enabled = handoffEnabled(flag)
  const trailingSlash = publicConfig('useTrailingSlash')
  const [mounted, setMounted] = useState(false)
  // New tab or same tab: decided once, after mount (the server render is same-tab, so hydration
  // sees the same attributes and copy; the next render upgrades a desktop to a new tab).
  const [newTab, setNewTab] = useState(false)
  useEffect(() => {
    // Flag off: no extra render for the host.
    if (enabled) {
      setMounted(true)
      setNewTab(prefersNewTab(window))
    }
  }, [enabled])
  // Read at render once mounted: a host reused across a client-side navigation links the page
  // it is on now (b35f598e).
  const path = enabled && mounted ? window.location.pathname : null
  return useMemo(
    () => writerHandoffLink({ enabled, src, productHref, path, hash, trailingSlash, newTab }),
    [enabled, src, productHref, path, hash, trailingSlash, newTab]
  )
}

/** The hook's rule, pure: null = inline; else the writer link, `from` = `path` (+ `#hash`). */
export function writerHandoffLink(input: {
  enabled: boolean
  src: WriterSource
  productHref: string | null | undefined
  /** The page's own path; null on the server and the hydrating render. */
  path: string | null
  hash?: string
  trailingSlash: boolean
  /** Desktop after mount (`prefersNewTab`); false on the server and the hydrating render. */
  newTab?: boolean
}): WriterHandoffLink | null {
  const slug = productSlugFromHref(input.productHref)
  if (!input.enabled || !slug) {
    return null
  }
  const from = input.path ? `${input.path}${input.hash ? `#${input.hash}` : ''}` : null
  const newTab = Boolean(input.newTab)
  return {
    href: (buyAgain?: boolean) =>
      writerHref(
        { product: slug, buyAgain: buyAgain ?? null, src: input.src, from, newTab },
        input.trailingSlash
      ),
    newTab,
    linkProps: newTab ? { target: '_blank', rel: 'noopener' } : {},
    hint: handoffHint(input.src, newTab),
  }
}
